import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Users as UsersIcon, Plus, Trash2, ShieldCheck, User as UserIcon, Loader2, KeyRound, Coins, Pencil } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { listUsers, createUser, deleteUser, resetUserPassword, grantCredits, updateUserName, updateUserRole } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useToast } from "../hooks/use-toast";

const emptyForm = { name: "", email: "", password: "", role: "staff" };

const UsersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [newPass, setNewPass] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [creditUser, setCreditUser] = useState(null);
  const [creditAmt, setCreditAmt] = useState(1);
  const [creditBusy, setCreditBusy] = useState(false);
  const [nameUser, setNameUser] = useState(null);
  const [newName, setNewName] = useState("");
  const [nameBusy, setNameBusy] = useState(false);

  const isAdmin = user?.role === "admin";
  const isOwner = (user?.email || "").toLowerCase() === "ngkc93@gmail.com";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setUsers(await listUsers());
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Could not load users.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (!isAdmin) { navigate("/"); return; }
    load();
  }, [isAdmin, load, navigate]);

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) {
      toast({ title: "Name, email and password are required.", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      await createUser({ name: form.name.trim(), email: form.email.trim(), password: form.password, role: form.role });
      toast({ title: "User added", description: form.email.trim() });
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Could not add user.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteUser(toDelete.id);
      toast({ title: "User removed", description: toDelete.email });
      setToDelete(null);
      load();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Could not remove user.", variant: "destructive" });
      setToDelete(null);
    }
  };

  const handleRename = async () => {
    if (!nameUser) return;
    if (!newName.trim()) {
      toast({ title: "Name cannot be empty.", variant: "destructive" });
      return;
    }
    setNameBusy(true);
    try {
      await updateUserName(nameUser.id, newName.trim());
      toast({ title: "Name updated", description: newName.trim() });
      setNameUser(null);
      setNewName("");
      load();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Could not update name.", variant: "destructive" });
    } finally {
      setNameBusy(false);
    }
  };

  const handleAddCredits = async () => {    if (!creditUser) return;
    if (Number(creditAmt) < 1) {
      toast({ title: "Credits must be at least 1.", variant: "destructive" });
      return;
    }
    setCreditBusy(true);
    try {
      const res = await grantCredits(creditUser.email, creditAmt);
      toast({ title: "Credits added", description: `${res.creditsAdded} credit(s) added to ${creditUser.email}. Balance: ${res.credits}.` });
      setCreditUser(null);
      setCreditAmt(1);
      load();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Could not add credits.", variant: "destructive" });
    } finally {
      setCreditBusy(false);
    }
  };

  const handleRole = async (u, role) => {
    if (!u || u.role === role) return;
    try {
      await updateUserRole(u.id, role);
      toast({ title: "Role updated", description: `${u.email} is now ${role}.` });
      load();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Could not update role.", variant: "destructive" });
    }
  };

  const handleReset = async () => {
    if (!resetUser) return;
    if (!newPass || newPass.length < 4) {
      toast({ title: "Password must be at least 4 characters.", variant: "destructive" });
      return;
    }
    setResetBusy(true);
    try {
      await resetUserPassword(resetUser.id, newPass);
      toast({ title: "Password updated", description: resetUser.email });
      setResetUser(null);
      setNewPass("");
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Could not reset password.", variant: "destructive" });
    } finally {
      setResetBusy(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="max-w-[1100px] mx-auto">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded bg-[#1877c9] text-white flex items-center justify-center">
          <UsersIcon size={20} />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-gray-800">Users</h1>
          <p className="text-sm text-gray-500">Manage staff logins and access levels</p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm shadow-sm">
        <div className="flex items-center justify-between gap-3 p-4 border-b border-gray-100">
          <div className="text-sm text-gray-500">{users.length} {users.length === 1 ? "user" : "users"}</div>
          <Button onClick={() => { setForm(emptyForm); setOpen(true); }} className="bg-[#1877c9] hover:bg-[#0f5ea3] gap-2" data-testid="add-user-btn">
            <Plus size={16} /> Add User
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 text-gray-500 py-14"><Loader2 className="animate-spin" size={18} /> Loading users…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#f7f9fc] text-gray-600 text-left">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">Credits</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-gray-100 hover:bg-[#f9fbfe]" data-testid={`user-row-${u.email}`}>
                    <td className="px-4 py-3 font-medium text-gray-800">{u.name}</td>
                    <td className="px-4 py-3 text-gray-600">{u.email}</td>
                    <td className="px-4 py-3">
                      {isOwner && (u.email || "").toLowerCase() !== "ngkc93@gmail.com" ? (
                        <Select value={u.role || "staff"} onValueChange={(v) => handleRole(u, v)}>
                          <SelectTrigger className="h-8 w-[110px] text-xs" data-testid={`role-select-${u.email}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="staff">staff</SelectItem>
                            <SelectItem value="admin">admin</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${u.role === "admin" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>
                          {u.role === "admin" ? <ShieldCheck size={12} /> : <UserIcon size={12} />}
                          {u.role || "staff"}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {(u.email || "").toLowerCase() === "ngkc93@gmail.com" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700" data-testid={`user-credits-${u.email}`}>
                          <Coins size={12} /> Unlimited
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-200" data-testid={`user-credits-${u.email}`}>
                          <Coins size={12} /> {Number(u.credits || 0)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center justify-end gap-1">
                        {isOwner && (u.email || "").toLowerCase() !== "ngkc93@gmail.com" && (
                          <button
                            onClick={() => { setCreditUser(u); setCreditAmt(1); }}
                            title="Add credits"
                            className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded"
                            data-testid={`add-credits-${u.email}`}
                          >
                            <Coins size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => { setNameUser(u); setNewName(u.name || ""); }}
                          title="Edit name"
                          className="p-1.5 text-gray-500 hover:text-[#1877c9] hover:bg-blue-50 rounded"
                          data-testid={`edit-name-${u.email}`}
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => { setResetUser(u); setNewPass(""); }}
                          title="Reset password"
                          className="p-1.5 text-gray-500 hover:text-[#1877c9] hover:bg-blue-50 rounded"
                          data-testid={`reset-password-${u.email}`}
                        >
                          <KeyRound size={16} />
                        </button>
                        <button
                          onClick={() => setToDelete(u)}
                          disabled={u.id === user.id}
                          title={u.id === user.id ? "You cannot delete yourself" : "Remove user"}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`delete-user-${u.email}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add user dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Full Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Jane Tan" data-testid="user-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@example.com" data-testid="user-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Set an initial password" data-testid="user-password" />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff (no user management)</SelectItem>
                  <SelectItem value="admin">Admin (full access)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={busy} className="bg-[#1877c9] hover:bg-[#0f5ea3]" data-testid="user-save-btn">
              {busy ? "Adding…" : "Add User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetUser} onOpenChange={(o) => { if (!o) { setResetUser(null); setNewPass(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-gray-500">Set a new password for <span className="font-medium text-gray-700">{resetUser?.name} ({resetUser?.email})</span>. They can log in with it immediately.</p>
            <div className="space-y-1.5">
              <Label>New Password</Label>
              <Input type="text" value={newPass} onChange={(e) => setNewPass(e.target.value)} placeholder="Enter a new password" data-testid="reset-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetUser(null); setNewPass(""); }}>Cancel</Button>
            <Button onClick={handleReset} disabled={resetBusy} className="bg-[#1877c9] hover:bg-[#0f5ea3]" data-testid="reset-password-save">
              {resetBusy ? "Saving…" : "Update Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename user dialog */}
      <Dialog open={!!nameUser} onOpenChange={(o) => { if (!o) { setNameUser(null); setNewName(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit name</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-gray-500">Change the display name for <span className="font-medium text-gray-700">{nameUser?.email}</span>.</p>
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Specfield" data-testid="edit-name-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNameUser(null); setNewName(""); }}>Cancel</Button>
            <Button onClick={handleRename} disabled={nameBusy} className="bg-[#1877c9] hover:bg-[#0f5ea3]" data-testid="edit-name-save">
              {nameBusy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add credits dialog */}
      <Dialog open={!!creditUser} onOpenChange={(o) => { if (!o) { setCreditUser(null); setCreditAmt(1); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add credits</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-gray-500">Add tax-file credits to <span className="font-medium text-gray-700">{creditUser?.name} ({creditUser?.email})</span>. Current balance: <span className="font-medium">{Number(creditUser?.credits || 0)}</span>.</p>
            <div className="space-y-1.5">
              <Label>Credits to add</Label>
              <Input type="number" min={1} value={creditAmt} onChange={(e) => setCreditAmt(e.target.value)} className="text-right font-mono" data-testid="add-credits-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreditUser(null); setCreditAmt(1); }}>Cancel</Button>
            <Button onClick={handleAddCredits} disabled={creditBusy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-credits-save">
              {creditBusy ? "Adding…" : "Add credits"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove user?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete ? `This will permanently remove ${toDelete.name} (${toDelete.email}). They will no longer be able to log in.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-user">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersPage;
